
import React, { Component } from "react";
import PropTypes from "prop-types";
import interact from "interactjs";

export default class RectDrawer extends Component {
    constructor(props) {
        super(props);
        this.state = {
            rect: props.value || { x: 100, y: 100, w: 200, h: 150, rotation: 0 }
        };

        this.boxRef = React.createRef();
        this.handleRef = React.createRef();
        this.containerRef = React.createRef();
    }

    componentDidMount() {
        this.initInteractions();
    }

    componentWillUnmount() {
        // Clean up interact.js instances
        if (this.boxRef.current) {
            interact(this.boxRef.current).unset();
        }
        if (this.handleRef.current) {
            interact(this.handleRef.current).unset();
        }
    }

    updateRect = (newRect) => {
        this.setState({ rect: newRect }, () => {
            // Send update to Dash
            if (this.props.setProps) {
                this.props.setProps({ value: newRect });
            }
        });
    };

    handleSave = () => {
        if (this.props.setProps) {
            this.props.setProps({
                value: this.state.rect,
                n_clicks: (this.props.n_clicks || 0) + 1
            });
        }
    };

    adjustWidth = (delta) => {
        const { rect } = this.state;
        const newWidth = Math.max(20, rect.w + delta);
        const widthChange = newWidth - rect.w;

        // Convert angle to radians
        const rotRad = (rect.rotation * Math.PI) / 180;

        // Adjust position to keep center fixed
        this.updateRect({
            ...rect,
            w: newWidth,
            x: rect.x - (widthChange / 2) * Math.cos(rotRad),
            y: rect.y - (widthChange / 2) * Math.sin(rotRad)
        });
    };

    adjustHeight = (delta) => {
        const { rect } = this.state;
        const newHeight = Math.max(20, rect.h + delta);
        const heightChange = newHeight - rect.h;

        // Convert angle to radians
        const rotRad = (rect.rotation * Math.PI) / 180;

        // Adjust position to keep center fixed
        this.updateRect({
            ...rect,
            h: newHeight,
            x: rect.x + (heightChange / 2) * Math.sin(rotRad),
            y: rect.y - (heightChange / 2) * Math.cos(rotRad)
        });
    };

    initInteractions = () => {
        const box = this.boxRef.current;
        const container = this.containerRef.current;

        // Draggable and resizable with boundaries
        interact(box)
            .draggable({
                modifiers: [
                    interact.modifiers.restrict({
                        restriction: container,
                        elementRect: { top: 0, left: 0, bottom: 1, right: 1 }
                    })
                ],
                listeners: {
                    move: (event) => {
                        this.updateRect({
                            ...this.state.rect,
                            x: this.state.rect.x + event.dx,
                            y: this.state.rect.y + event.dy
                        });
                    }
                }
            })
            .resizable({
                edges: { top: true, left: true, bottom: true, right: true },
                modifiers: [
                    interact.modifiers.restrictEdges({
                        outer: container
                    }),
                    interact.modifiers.restrictSize({
                        min: { width: 20, height: 20 }
                    })
                ],
                listeners: {
                    move: (event) => {
                        const { rect } = this.state;

                        // Update position and size based on delta
                        this.updateRect({
                            ...rect,
                            x: rect.x + event.deltaRect.left,
                            y: rect.y + event.deltaRect.top,
                            w: event.rect.width,
                            h: event.rect.height
                        });
                    }
                }
            });

        // Rotation handle
        const handle = this.handleRef.current;
        interact(handle).draggable({
            listeners: {
                move: (event) => {
                    const r = this.state.rect;
                    const containerRect = container.getBoundingClientRect();

                    // Calculate center of the box relative to viewport
                    const cx = containerRect.left + r.x + r.w / 2;
                    const cy = containerRect.top + r.y + r.h / 2;

                    // Calculate angle from center to mouse position
                    const angle = Math.atan2(
                        event.clientY - cy,
                        event.clientX - cx
                    ) * 180 / Math.PI;

                    this.updateRect({ ...r, rotation: angle });
                }
            }
        });
    };

    render() {
        const r = this.state.rect;
        const { id, image, width, height, style, stepSize } = this.props;

        const containerStyle = {
            position: "relative",
            width: width || "800px",
            height: height || "600px",
            background: image ? `url(${image}) no-repeat center/contain` : "#eee",
            overflow: "hidden", // Prevent overflow
            ...style
        };

        const buttonStyle = {
            padding: "6px 12px",
            background: "#2196F3",
            color: "white",
            border: "none",
            borderRadius: "4px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "bold",
            margin: "2px"
        };

        return (
            <div id={id} ref={this.containerRef} style={containerStyle}>
                {/* Rectangle */}
                <div
                    ref={this.boxRef}
                    style={{
                        position: "absolute",
                        top: r.y,
                        left: r.x,
                        width: r.w,
                        height: r.h,
                        border: "4px solid black",
                        transform: `rotate(${r.rotation}deg)`,
                        transformOrigin: "center",
                        touchAction: "none",
                        boxSizing: "border-box"
                    }}
                >
                    {/* Rotation handle */}
                    <div
                        ref={this.handleRef}
                        style={{
                            position: "absolute",
                            top: "-25px",
                            left: "50%",
                            transform: "translateX(-50%)",
                            width: "20px",
                            height: "20px",
                            borderRadius: "50%",
                            background: "blue",
                            cursor: "grab",
                            touchAction: "none"
                        }}
                    />
                </div>

                {/* Size control buttons */}
                <div
                    style={{
                        position: "absolute",
                        top: "10px",
                        left: "10px",
                        display: "flex",
                        flexDirection: "column",
                        gap: "5px",
                        zIndex: 10
                    }}
                >
                    <div style={{ display: "flex", gap: "5px" }}>
                        <button
                            onClick={() => this.adjustWidth(stepSize || 10)}
                            style={buttonStyle}
                            title="Aumentar - Largura"
                        >
                            L+
                        </button>
                        <button
                            onClick={() => this.adjustWidth(-(stepSize || 10))}
                            style={buttonStyle}
                            title="Reduzir - Largura"
                        >
                            L-
                        </button>
                    </div>
                    <div style={{ display: "flex", gap: "5px" }}>
                        <button
                            onClick={() => this.adjustHeight(stepSize || 10)}
                            style={buttonStyle}
                            title="Aumentar - Altura"
                        >
                            A+
                        </button>
                        <button
                            onClick={() => this.adjustHeight(-(stepSize || 10))}
                            style={buttonStyle}
                            title="Reduzir - Altura"
                        >
                            A-
                        </button>
                    </div>
                </div>

                {/* Save button */}
                <button
                    onClick={this.handleSave}
                    style={{
                        position: "absolute",
                        top: "10px",
                        right: "10px",
                        padding: "8px 16px",
                        background: "#4CAF50",
                        color: "white",
                        border: "none",
                        borderRadius: "4px",
                        cursor: "pointer",
                        fontSize: "14px",
                        fontWeight: "bold",
                        zIndex: 10
                    }}
                >
                    Gravar
                </button>
            </div>
        );
    }
}

RectDrawer.defaultProps = {
    value: { x: 100, y: 100, w: 200, h: 150, rotation: 0 },
    width: "640px",
    height: "640px",
    n_clicks: 0,
    stepSize: 10
};

RectDrawer.propTypes = {
    /**
     * The ID used to identify this component in Dash callbacks.
     */
    id: PropTypes.string,

    /**
     * Rectangle data: {x, y, w, h, rotation}
     */
    value: PropTypes.shape({
        x: PropTypes.number,
        y: PropTypes.number,
        w: PropTypes.number,
        h: PropTypes.number,
        rotation: PropTypes.number
    }),

    /**
     * Image URL to display as background
     */
    image: PropTypes.string,

    /**
     * Container width
     */
    width: PropTypes.string,

    /**
     * Container height
     */
    height: PropTypes.string,

    /**
     * Additional CSS styles for container
     */
    style: PropTypes.object,

    /**
     * Number of times the save button has been clicked
     */
    n_clicks: PropTypes.number,

        /**
     * Step size for width/height adjustments (default: 10)
     */
    stepSize: PropTypes.number,

    /**
     * Dash-assigned callback that should be called to report property changes
     * to Dash, to make them available for callbacks.
     */
    setProps: PropTypes.func
};